import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Loader2, Send } from "lucide-react";

export function EmailInvoiceButton({ legacyId, defaultEmail, className, open: openProp, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = controlled ? onOpenChange : setOpenState;
  const [to, setTo] = useState(defaultEmail || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!to.includes("@")) { toast.error("Enter the customer's email address"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${legacyId}/invoice-email`, { to: to.trim(), message: msg });
      toast.success(`Invoice ${r.data.invoice_no} emailed to ${r.data.to}`);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send invoice");
    } finally { setBusy(false); }
  };

  return (
    <>
      {!controlled && <button className={className} onClick={() => { setTo(defaultEmail || ""); setMsg(""); setOpen(true); }} data-testid="job-ql-email-invoice">
        <Mail className="w-3.5 h-3.5" /> Email invoice
      </button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invoice-email-dialog">
          <DialogHeader><DialogTitle>Email invoice to customer</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Customer email</span>
              <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" className="mt-1" data-testid="invoice-email-to" />
            </label>
            {!defaultEmail && <p className="text-xs text-amber-600" data-testid="invoice-email-no-default">No email on file for this job's customer — type one in.</p>}
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Message (optional)</span>
              <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} data-testid="invoice-email-message"
                className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm" />
            </label>
            <p className="text-[11px] text-slate-400">The invoice PDF is attached. A sequential invoice number is assigned the first time it's issued.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" onClick={send} disabled={busy} data-testid="invoice-email-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
